import { useParams } from "react-router-dom";
import { getProject } from "../../../services/projectService";
import { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { CheckIcon, CloseIcon, SettingsIcon } from "../../../icons";
import { t } from "i18next";
import { ContainedButtonStyles } from "../../../styles/GlobalStyles";
import { toast } from "react-toastify";

interface LabelSettingsProps {
  labelList: string[];
  tempSelectedLabels: string[];
  setTempSelectedLabels: (tempSelectedLabels: string[]) => void;
}

export default function LabelSettings({ labelList, tempSelectedLabels, setTempSelectedLabels }: LabelSettingsProps) {
  const handleSelect = (label: string) => {
    if (tempSelectedLabels.includes(label)) {
      const currentLabels = tempSelectedLabels.filter((item) => item !== label);
      console.log(currentLabels);
      setTempSelectedLabels(currentLabels);
    } else {
      setTempSelectedLabels([...tempSelectedLabels, label]);
    }
  };

  const handleSelectAll = () => {
    setTempSelectedLabels(labelList);
  };

  const handleDeselect = () => {
    setTempSelectedLabels([]);
  };

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "18px",
          py: "14px",
          borderBottom: "1px solid #f3f4f6",
          background: "#fafafa",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: "6px",
              background: "#f3f4f6",
              border: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SettingsIcon sx={{ fontSize: 14, color: "#6b7280" }} />
          </Box>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{t("ClassSelect")}</Typography>
          <Box
            sx={{
              px: "7px",
              py: "1px",
              borderRadius: "20px",
              background: tempSelectedLabels.length > 0 ? "#eff6ff" : "#f3f4f6",
              border: `1px solid ${tempSelectedLabels.length > 0 ? "#bfdbfe" : "#e5e7eb"}`,
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: tempSelectedLabels.length > 0 ? "#3b82f6" : "#9ca3af",
              }}
            >
              {tempSelectedLabels.length} / {labelList.length}
            </Typography>
          </Box>
        </Box>

        <button
          onClick={tempSelectedLabels.length === labelList.length ? handleDeselect : handleSelectAll}
          style={{
            padding: "4px 12px",
            borderRadius: "7px",
            border: "1px solid #e5e7eb",
            background: "#fff",
            fontSize: "12px",
            fontWeight: 500,
            color: "#374151",
            cursor: "pointer",
            transition: "all .15s",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor = "#3b82f6";
            (e.currentTarget as HTMLElement).style.color = "#3b82f6";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor = "#e5e7eb";
            (e.currentTarget as HTMLElement).style.color = "#374151";
          }}
        >
          {tempSelectedLabels.length === labelList.length ? t("DeselectAll") : t("SelectAll")}
        </button>
      </Box>
      <Box sx={{ p: "14px", display: "flex", flexDirection: "column", gap: "6px" }}>
        {labelList.map((label) => {
          const isSelected = tempSelectedLabels.includes(label);
          return (
            <Box
              key={label}
              onClick={() => handleSelect(label)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: "14px",
                py: "10px",
                borderRadius: "9px",
                border: isSelected ? "1px solid #bfdbfe" : "1px solid #e5e7eb",
                background: isSelected ? "#f0f7ff" : "#fafafa",
                cursor: "pointer",
                transition: "all .15s",
                "&:hover": {
                  borderColor: isSelected ? "#93c5fd" : "#d1d5db",
                  background: isSelected ? "#e8f3ff" : "#f3f4f6",
                },
              }}
            >
              <Typography
                sx={{ fontSize: "13px", fontWeight: isSelected ? 600 : 400, color: isSelected ? "#1d4ed8" : "#374151" }}
              >
                {label}
              </Typography>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: isSelected ? "#3b82f6" : "#e5e7eb",
                  transition: "all .15s",
                  flexShrink: 0,
                }}
              >
                {isSelected ? (
                  <CheckIcon sx={{ fontSize: 13, color: "#fff" }} />
                ) : (
                  <CloseIcon sx={{ fontSize: 13, color: "#9ca3af" }} />
                )}
              </Box>
            </Box>
          );
        })}
        <Box
          sx={{
            mt: "4px",
            px: "12px",
            py: "9px",
            borderRadius: "8px",
            background: tempSelectedLabels.length === 0 ? "#fff5f5" : "#f9fafb",
            border: `1px solid ${tempSelectedLabels.length === 0 ? "#fecaca" : "#f3f4f6"}`,
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {tempSelectedLabels.length === 0 ? (
            <CloseIcon sx={{ fontSize: 13, color: "#ef4444" }} />
          ) : (
            <CheckIcon sx={{ fontSize: 13, color: "#22c55e" }} />
          )}
          <Typography sx={{ fontSize: "11px", color: tempSelectedLabels.length === 0 ? "#ef4444" : "#6b7280" }}>
            {tempSelectedLabels.length === 0
              ? t("MsgMinOneClassRequired")
              : `${tempSelectedLabels.length}개 클래스 선택됨 · ${t("MsgIncludeSelectedClasses")}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
